import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import Skeleton, { SkeletonTheme } from 'react-loading-skeleton';
import { Document, Page, pdfjs } from 'react-pdf';
import SharePreviewToolbar from 'components/ShareDocument/SharePreviewContent/SharePreviewToolbar';
import { SharePreviewProps } from 'components/ShareDocument/SharePreviewContent/sharePreviewHelpers';

import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';

pdfjs.GlobalWorkerOptions.workerSrc = new URL('pdfjs-dist/build/pdf.worker.min.mjs', import.meta.url).toString();

const ZOOM_MIN = 0.5;
const ZOOM_MAX = 2;
const ZOOM_STEP = 0.1;

interface PdfNavButtonProps {
  direction: 'prev' | 'next';
  disabled: boolean;
  onClick: () => void;
}

const PdfNavButton = ({ direction, disabled, onClick }: PdfNavButtonProps) => (
  <button
    type='button'
    className={`general-button ${direction === 'prev' ? 'total-contributions-prev' : 'total-contributions-next'}`}
    disabled={disabled}
    onClick={onClick}
    aria-label={direction === 'prev' ? 'Previous page' : 'Next page'}
    style={{ width: '22px', height: '22px' }}
  >
    <span className={direction === 'prev' ? 'icon-arrow-back' : 'icon-arrow-next'} />
  </button>
);

interface PdfZoomControlsProps {
  zoom: number;
  onChange: (nextZoom: number) => void;
}

const PdfZoomControls = ({ zoom, onChange }: PdfZoomControlsProps) => (
  <div className='d-flex align-items-center border specific-y-padding-5 specific-x-padding-4 h-100 rounded-pill total-contributions-buttons bg-white'>
    <button
      type='button'
      className='general-button d-flex align-items-center justify-content-center paragraph-p3'
      disabled={zoom <= ZOOM_MIN}
      onClick={() => onChange(zoom - ZOOM_STEP)}
      aria-label='Zoom out'
      style={{ width: '22px', height: '22px' }}
    >
      <span className='d-inline-block-centered centered-4'>−</span>
    </button>
    <span className='nowrap-item paragraph-p3 main-subtle-text mx-3'>{Math.round(zoom * 100)}%</span>
    <button
      type='button'
      className='general-button d-flex align-items-center justify-content-center paragraph-p3'
      disabled={zoom >= ZOOM_MAX}
      onClick={() => onChange(zoom + ZOOM_STEP)}
      aria-label='Zoom in'
      style={{ width: '22px', height: '22px' }}
    >
      <span className='d-inline-block-centered centered-4'>+</span>
    </button>
  </div>
);

interface PdfPageControlsProps {
  page: number;
  pages: number;
  onGoToPage: (page: number) => void;
}

const PdfPageControls = ({ page, pages, onGoToPage }: PdfPageControlsProps) => (
  <div className='d-flex align-items-center border specific-top-padding-4 specific-bottom-padding-5 specific-x-padding-8 h-100 rounded-pill total-contributions-buttons bg-white'>
    <PdfNavButton direction='prev' disabled={page <= 1} onClick={() => onGoToPage(page - 1)} />
    <span className='nowrap-item paragraph-p3 main-subtle-text mx-3'>
      Page {page} of {pages}
    </span>
    <PdfNavButton direction='next' disabled={page >= pages} onClick={() => onGoToPage(page + 1)} />
  </div>
);

const PdfPreviewSkeleton = () => (
  <div className='p-3'>
    <SkeletonTheme baseColor='#f2f2f2'>
      <div
        className='bg-white border-general border-grey-50 rounded mx-auto specific-y-padding-40 specific-x-padding-32'
        style={{ maxWidth: '640px' }}
      >
        <Skeleton height={28} width='55%' className='mb-4' />
        <Skeleton count={5} className='mb-2' />
        <Skeleton width='80%' className='mb-4' />
        <Skeleton count={6} className='mb-2' />
        <Skeleton width='40%' className='mb-4' />
        <Skeleton count={4} className='mb-2' />
        <Skeleton width='65%' />
      </div>
    </SkeletonTheme>
  </div>
);

const PdfPreview = ({ url, filename, barBackgroundColor }: SharePreviewProps) => {
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(0);
  const [zoom, setZoom] = useState(1);
  const [containerWidth, setContainerWidth] = useState<number | null>(null);
  const [toolbarHeight, setToolbarHeight] = useState(0);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const pageRefs = useRef<Record<number, HTMLDivElement | null>>({});

  useEffect(() => {
    setPage(1);
    setPages(0);
    setZoom(1);
    pageRefs.current = {};
  }, [url]);

  useLayoutEffect(() => {
    const updateWidth = () => {
      const width = scrollContainerRef.current?.clientWidth;

      if (width && width > 0) {
        setContainerWidth(width);
      }
    };

    updateWidth();

    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', updateWidth);
      return () => window.removeEventListener('resize', updateWidth);
    }

    const element = scrollContainerRef.current;

    if (!element) {
      return;
    }

    const resizeObserver = new ResizeObserver(updateWidth);
    resizeObserver.observe(element);

    return () => resizeObserver.disconnect();
  }, [url]);

  useLayoutEffect(() => {
    const element = scrollContainerRef.current?.previousElementSibling;

    if (!element) {
      return;
    }

    const updateHeight = () => setToolbarHeight(element.getBoundingClientRect().height);

    updateHeight();

    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', updateHeight);
      return () => window.removeEventListener('resize', updateHeight);
    }

    const resizeObserver = new ResizeObserver(updateHeight);
    resizeObserver.observe(element);

    return () => resizeObserver.disconnect();
  }, [pages]);

  useEffect(() => {
    if (pages === 0) {
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        const mostVisible = entries
          .filter((entry) => entry.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];

        const visiblePage = Number(mostVisible?.target.getAttribute('data-page-number'));

        if (visiblePage) {
          setPage(visiblePage);
        }
      },
      { threshold: [0.5] }
    );

    Object.values(pageRefs.current).forEach((element) => element && observer.observe(element));

    return () => observer.disconnect();
  }, [pages]);

  const goToPage = (nextPage: number) => {
    setPage(nextPage);
    pageRefs.current[nextPage]?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const changeZoom = (nextZoom: number) => {
    const clampedZoom = Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, +nextZoom.toFixed(2)));
    const container = scrollContainerRef.current;

    if (container && clampedZoom !== zoom) {
      const ratio = clampedZoom / zoom;
      const targetScrollLeft = (container.scrollLeft + container.clientWidth / 2) * ratio - container.clientWidth / 2;
      const targetScrollTop = (container.scrollTop + container.clientHeight / 2) * ratio - container.clientHeight / 2;

      requestAnimationFrame(() => {
        container.scrollLeft = targetScrollLeft;
        container.scrollTop = targetScrollTop;
      });
    }

    setZoom(clampedZoom);
  };

  return (
    <div className='rounded'>
      <SharePreviewToolbar
        url={url}
        filename={filename}
        barBackgroundColor={barBackgroundColor}
        variant='grid'
        left={pages > 0 ? <PdfZoomControls zoom={zoom} onChange={changeZoom} /> : null}
        right={pages > 0 ? <PdfPageControls page={page} pages={pages} onGoToPage={goToPage} /> : null}
      />
      <div className='overflow-auto attachment-pdf-fill-preview rounded-bottom border-grey-50' ref={scrollContainerRef}>
        <Document
          file={url}
          onLoadSuccess={({ numPages }) => {
            setPages(numPages);
            setPage((prev) => Math.min(prev, numPages || 1));
          }}
          loading={<PdfPreviewSkeleton />}
          error={<p className='paragraph-general mb-0 p-3'>Unable to preview this PDF file.</p>}
        >
          {Array.from({ length: pages }, (_, index) => {
            const pageNumber = index + 1;

            return (
              <div
                key={`page-${pageNumber}`}
                ref={(el) => {
                  pageRefs.current[pageNumber] = el;
                }}
                data-page-number={pageNumber}
                style={{ scrollMarginTop: `${toolbarHeight}px` }}
              >
                <Page
                  pageNumber={pageNumber}
                  width={containerWidth ? containerWidth * zoom : undefined}
                  renderAnnotationLayer={false}
                  renderTextLayer={false}
                  className='attachment-pdf-page'
                />
              </div>
            );
          })}
        </Document>
      </div>
    </div>
  );
};

export default PdfPreview;
